import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEdit } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import { formatDateDDMMYY } from '../../../components/common/dateUtils';

const ROWS_PER_PAGE = 34;

const BillOfLadingList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  // Fetch paginated data from server
  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/import/bill-of-lading-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const result = await res.json();
      setRecords(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch bill of lading error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="max-w-8xl mx-auto p-3 sm:p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3 sm:gap-2">
        <h2 className="text-lg sm:text-xl font-semibold text-center sm:text-left">Bill of Lading Records</h2>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-1 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Search by B/L No, vessel, container..."
            value={search}
            onChange={handleSearchChange}
            className="border border-gray-200 rounded px-3 sm:px-2 py-2 sm:py-1 focus:outline-none w-full sm:w-48 text-sm"
          />
          <button
            className="flex items-center justify-center gap-1 bg-[#3899cf] text-white px-3 py-2 sm:py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium w-full sm:w-auto"
            onClick={() => navigate('/bill-of-lading/new')}
          >
            <FaPlus />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No bill of lading records found." />
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
               
                <th className="px-3 py-2 text-left font-medium">Purchase Contract No</th>
                <th className="px-3 py-2 text-left font-medium">B/L No</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Order ID</th>
                <th className="px-3 py-2 text-left font-medium">Issue Date</th>
                <th className="px-3 py-2 text-left font-medium">Vessel Name</th>
                <th className="px-3 py-2 text-left font-medium">Origin Port</th>
                <th className="px-3 py-2 text-left font-medium">Origin Country</th>
                <th className="px-3 py-2 text-left font-medium">Departure Date</th>
                <th className="px-3 py-2 text-left font-medium">ETA Port</th>
                <th className="px-3 py-2 text-left font-medium">Arrival Date</th>
                <th className="px-3 py-2 text-left font-medium">Berthing Date</th>
                <th className="px-3 py-2 text-left font-medium">Container No</th>
                <th className="px-3 py-2 text-left font-medium">Gross Weight (MT)</th>
               <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item, idx) => (
                <tr key={item.bl_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
               
                  <td className="px-3 py-1">{item.purchase_contract_no || ''}</td>
                  <td className="px-3 py-1 font-semibold">{item.bl_no}</td>
                  <td className="px-3 py-1">{item.bl_type}</td>
                  <td className="px-3 py-1">{item.order_id}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.issue_date)}</td>
                  <td className="px-3 py-1">{item.vessel_name}</td>
                  <td className="px-3 py-1">{item.origin_port}</td>
                  <td className="px-3 py-1">{item.origin_country}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.departure_date)}</td>
                  <td className="px-3 py-1">{item.eta_port}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.arrival_date)}</td>
                  <td className="px-3 py-1">{formatDateDDMMYY(item.berthing_date)}</td>
                  <td className="px-3 py-1">{item.container_no}</td>
                  <td className="px-3 py-1">
                    {typeof item.gross_weight_mt === 'number'
                      ? item.gross_weight_mt.toFixed(3)
                      : item.gross_weight_mt || ''}
                  </td>
                     <td className="px-3 py-1">
                    <button
                      onClick={() => navigate(`/bill-of-lading/edit/${item.bl_id}`)}
                      className="flex items-center gap-1 bg-[#28a745] text-white px-2 py-1 text-xs rounded hover:bg-[#218838] border border-[#28a745] font-medium"
                      title="Edit Record"
                    >
                      <FaEdit />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {total > ROWS_PER_PAGE && (
        <div className="flex justify-end items-center gap-2 mt-3 text-xs">
          <button
            onClick={handlePrevPage}
            disabled={page === 1}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === 1 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Prev
          </button>
          <span className="text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={page === totalPages}
            className={`px-2 py-1 rounded border transition-colors duration-100 ${
              page === totalPages 
                ? 'bg-gray-100 text-gray-300 border-gray-200' 
                : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BillOfLadingList;
